import React, { useEffect, useMemo, useState } from 'react';
import Points from 'components/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import { MONTHLY_MAINTENANCE_FEE_POINTS } from 'config/fees';
import { formatFeePct } from 'config/economyCopy';
import { FeeSummary } from 'types/interfaces/user';
import { eliteWaiverSentence } from '../FeatureCards/ZeroFeesCard';
import { CHECKOUT_SOURCES } from './constants';
import { FEES_COPY } from './copy';
import { clampFeeInput, computeFeeSavings, defaultSliderVolume, nearestStopIndex, stopsFor } from './feeMath';
import './sections.css';
import './ZeroFeesCalculator.css';

interface ZeroFeesCalculatorProps {
  ratePct: number | undefined;
  eliteDiscountPct: number | undefined;
  feeSummary: FeeSummary | undefined;
  isLoggedIn: boolean;
  isElite: boolean;
  avatarUrl?: string | null;
  username?: string;
  onSubscribe: (source: string) => void;
  isCheckoutLoading: boolean;
}

const COIN_URL = `${AssetUrls.base}/assets/icons/profile/coin.svg`;

const formatPoints = (value: number): string => value.toLocaleString('en-US');

const memberSinceLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

/**
 * ZeroFeesCalculator (Asana 1219949663037427, Decisions 6 to 8; visual pass and
 * range per Asana 1219988885932257). No fee number is a literal:
 * MONTHLY_MAINTENANCE_FEE_POINTS, the admin rate and the Elite discount drive
 * everything through computeFeeSavings. The slider is index-based over the
 * progressive stops; the typed value drives the math exactly and the thumb snaps
 * to the nearest stop.
 */
const ZeroFeesCalculator: React.FC<ZeroFeesCalculatorProps> = ({
  ratePct,
  eliteDiscountPct,
  feeSummary,
  isLoggedIn,
  isElite,
  avatarUrl,
  username,
  onSubscribe,
  isCheckoutLoading,
}) => {
  const avg = feeSummary?.avgMonthlyTradedPoints90d ?? null;
  const defaultVolume = useMemo(() => defaultSliderVolume(avg), [avg]);
  const stops = useMemo(() => stopsFor(defaultVolume), [defaultVolume]);
  const [volume, setVolume] = useState<number>(defaultVolume);
  const [adjusted, setAdjusted] = useState(false);

  // The summary arrives after mount; follow it until the trader changes the value.
  useEffect(() => {
    if (!adjusted) setVolume(defaultVolume);
  }, [defaultVolume, adjusted]);

  const loaded = ratePct != null && eliteDiscountPct != null;
  const savings = loaded
    ? computeFeeSavings({ volume, maintenanceFee: MONTHLY_MAINTENANCE_FEE_POINTS, ratePct, eliteDiscountPct })
    : null;

  const caption = adjusted ? FEES_COPY.captionAdjusted : avg != null ? FEES_COPY.captionMine : FEES_COPY.captionExample;
  const feesPaid = (feeSummary?.maintenanceFeesPaid ?? 0) + (feeSummary?.transactionFeesPaid ?? 0);
  const showSoFar = isLoggedIn && !isElite && !!feeSummary && feesPaid > 0;

  const amount = (value: number | undefined, className = '') =>
    savings && value !== undefined ? (
      <span className={`zero-fees__amount ${className}`.trim()}>
        <img src={COIN_URL} alt="" className="zero-fees__coin" />
        {formatPoints(value)}
      </span>
    ) : (
      <Points isLoading type="small" />
    );

  const transactionLabel = loaded ? `${formatFeePct(ratePct)} transaction fee` : 'Transaction fee';
  const transactionLabelShort = loaded ? `${formatFeePct(ratePct)} fee` : 'Fee';
  const discountLabel = loaded && !savings?.waived ? `${formatFeePct(eliteDiscountPct)} off` : null;

  const handleTyped = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setAdjusted(true);
    setVolume(digits ? clampFeeInput(Number(digits)) : 0);
  };

  const eliteRow = (value: number | undefined) =>
    savings?.waived ? (
      <span className="elite-compare-feature-row__waived">{FEES_COPY.waived}</span>
    ) : (
      <>
        {discountLabel && <span className="zero-fees__discount">{discountLabel}</span>}
        {amount(value)}
      </>
    );

  return (
    <section className="elite-section zero-fees" data-testid="zero-fees-calculator">
      <div className="zero-fees__panel">
        <div className="zero-fees__columns">
          <div className="zero-fees__pitch">
            <p className="elite-section__eyebrow elite-section__eyebrow--gold">{FEES_COPY.eyebrow}</p>
            <div className="zero-fee-pill" data-testid="zero-fee-pill" aria-label="0 points in fees">
              <div className="zero-fee-pill__inner">
                <img src={COIN_URL} alt="" className="zero-fee-pill__coin" />
                <span className="zero-fee-pill__value">0</span>
              </div>
            </div>
            <h2 className="elite-section__headline">{FEES_COPY.headline}</h2>
            <p className="elite-section__sub zero-fees__body">
              {eliteWaiverSentence(ratePct, eliteDiscountPct)} {FEES_COPY.lockIn}
            </p>
            <span className="zero-fees__chip">{FEES_COPY.chip}</span>
          </div>

          <div className="zero-fees__calc" data-testid="zero-fees-calc">
            <div className="zero-fees__slider-head">
              <span className="zero-fees__slider-label">{FEES_COPY.sliderLabel}</span>
              <span className="zero-fees__slider-value">
                <img src={COIN_URL} alt="" className="zero-fees__coin" />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9,]*"
                  className="zero-fees__value-input"
                  value={formatPoints(volume)}
                  aria-label="Points you trade each month, typed"
                  data-testid="zero-fees-value-input"
                  onChange={(e) => handleTyped(e.target.value)}
                />
              </span>
            </div>
            <input
              type="range"
              className="zero-fees__slider"
              min={0}
              max={stops.length - 1}
              step={1}
              value={nearestStopIndex(stops, volume)}
              aria-label={FEES_COPY.sliderLabel}
              aria-valuetext={`${formatPoints(volume)} points`}
              onChange={(e) => {
                setAdjusted(true);
                setVolume(stops[Number(e.target.value)] ?? 0);
              }}
            />
            <div className="zero-fees__caption">
              <span>{caption}</span>
              {adjusted && (
                <button
                  type="button"
                  className="zero-fees__reset"
                  onClick={() => {
                    setAdjusted(false);
                    setVolume(defaultVolume);
                  }}
                >
                  {FEES_COPY.reset}
                </button>
              )}
            </div>

            <div className="zero-fees__tiles">
              <div className="zero-fees__tile zero-fees__tile--free">
                <span className="zero-fees__tile-title">{FEES_COPY.freeTile}</span>
                <div className="zero-fees__row">
                  <span className="zero-fees__row-label">
                    <span className="zero-fees__label-long">{FEES_COPY.maintenance}</span>
                    <span className="zero-fees__label-short">{FEES_COPY.maintenanceShort}</span>
                  </span>
                  <span className="zero-fees__row-value">{amount(savings?.freeMaintenance)}/mo</span>
                </div>
                <div className="zero-fees__row">
                  <span className="zero-fees__row-label">
                    <span className="zero-fees__label-long">{transactionLabel}</span>
                    <span className="zero-fees__label-short">{transactionLabelShort}</span>
                  </span>
                  <span className="zero-fees__row-value">{amount(savings?.freeTransaction)}</span>
                </div>
                <div className="zero-fees__row zero-fees__row--total">
                  <span className="zero-fees__row-label">
                    <span className="zero-fees__label-long">{FEES_COPY.perMonth}</span>
                    <span className="zero-fees__label-short">{FEES_COPY.perMonthShort}</span>
                  </span>
                  <span className="zero-fees__row-value">{amount(savings?.freeMonthly, 'zero-fees__amount--total')}</span>
                </div>
              </div>

              <div className="zero-fees__tile zero-fees__tile--elite" data-testid="zero-fees-elite-tile">
                <span className="zero-fees__tile-title zero-fees__tile-title--elite">{FEES_COPY.eliteTile}</span>
                <div className="zero-fees__row">
                  <span className="zero-fees__row-label">
                    <span className="zero-fees__label-long">{FEES_COPY.maintenance}</span>
                    <span className="zero-fees__label-short">{FEES_COPY.maintenanceShort}</span>
                  </span>
                  <span className="zero-fees__row-value">{eliteRow(savings?.eliteMaintenance)}</span>
                </div>
                <div className="zero-fees__row">
                  <span className="zero-fees__row-label">
                    <span className="zero-fees__label-long">{transactionLabel}</span>
                    <span className="zero-fees__label-short">{transactionLabelShort}</span>
                  </span>
                  <span className="zero-fees__row-value">{eliteRow(savings?.eliteTransaction)}</span>
                </div>
                <div className="zero-fees__row zero-fees__row--total">
                  <span className="zero-fees__row-label">
                    <span className="zero-fees__label-long">{FEES_COPY.perMonth}</span>
                    <span className="zero-fees__label-short">{FEES_COPY.perMonthShort}</span>
                  </span>
                  <span className="zero-fees__row-value">{amount(savings?.eliteMonthly, 'zero-fees__amount--total')}</span>
                </div>
              </div>
            </div>

            <div className="zero-fees__savings">
              <span className="zero-fees__savings-label">{FEES_COPY.savings}</span>
              <span className="zero-fees__savings-value">{amount(savings?.yearlySavings, 'zero-fees__amount--savings')}</span>
            </div>
          </div>
        </div>

        {showSoFar && feeSummary && (
          <div className="zero-fees__sofar" data-testid="zero-fees-sofar">
            <div className="zero-fees__sofar-avatar">
              <AvatarWithFlag avatarUrl={avatarUrl ?? null} username={username || ''} size={44} alt="" />
            </div>
            <p className="zero-fees__sofar-copy">
              Since you joined in {memberSinceLabel(feeSummary.memberSince)}, you&apos;ve paid{' '}
              <Points type="inline" points={feesPaid} /> points in fees
              <span className="zero-fees__sofar-breakdown">
                : <Points type="inline" points={feeSummary.maintenanceFeesPaid} /> in maintenance and{' '}
                <Points type="inline" points={feeSummary.transactionFeesPaid} /> in transaction fees
              </span>
              . On Elite, that&apos;s <Points type="inline" points={0} />.
            </p>
            <button
              type="button"
              className="elite-subscribe-button elite-subscribe-button--shimmer zero-fees__sofar-cta"
              onClick={() => onSubscribe(CHECKOUT_SOURCES.fees)}
              disabled={isCheckoutLoading}
            >
              {isCheckoutLoading ? 'Loading...' : FEES_COPY.stopPaying}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ZeroFeesCalculator;
